import { useRef, useState } from "react"

import { Button } from "@coval/ui"
import { ChevronDown, ChevronUp } from "lucide-react"

import { openCoviWithPrompt } from "@/lib/coviEvents"
import { AskSofiaButton } from "@/components/AskSofiaButton"
import { formatRelativeTimestamp } from "@/utils/formatting/format-timestamp"

import type {
  InvestigationEvidenceLoad,
  InvestigationFinding,
  InvestigationFindingAction,
} from "../utils/investigationFindings"
import { FindingActionControls } from "./FindingActionControls"
import { FindingCard } from "./FindingCard"
import { FindingEvidenceList } from "./FindingEvidenceList"

function buildFindingReviewPrompt(finding: InvestigationFinding): string {
  return [
    `Help me review the scheduled investigation finding "${finding.title}".`,
    `Finding ID: ${finding.findingId}.`,
    `Category: ${finding.categoryLabel}.`,
    `Why it was prioritized: ${finding.briefingPriorityReason ?? "It was selected for today's briefing"}.`,
    "Use the persisted finding and its evidence to explain the impact, distinguish a real issue from expected testing behavior, and recommend the next concrete action.",
  ].join("\n")
}

interface HomeFindingRowProps {
  finding: InvestigationFinding
  loadEvidence: (
    finding: InvestigationFinding,
  ) => Promise<InvestigationEvidenceLoad | null>
  // Resolves true only when the action applied: anything the user typed survives a
  // conflict or a failure.
  onAction: (
    finding: InvestigationFinding,
    action: InvestigationFindingAction,
    note?: string,
  ) => Promise<boolean>
}

type EvidenceState =
  | { kind: "collapsed" }
  | { kind: "loading" }
  | { kind: "error" }
  | (InvestigationEvidenceLoad & { kind: "loaded" })

export function HomeFindingRow({
  finding,
  loadEvidence,
  onAction,
}: HomeFindingRowProps) {
  const [evidence, setEvidence] = useState<EvidenceState>({ kind: "collapsed" })
  // Every toggle takes the next ticket; a load that resolves after the user collapsed
  // the panel holds a stale one and is dropped instead of reopening it.
  const evidenceTicket = useRef(0)

  const evidenceOpen = evidence.kind !== "collapsed"

  const toggleEvidence = async () => {
    evidenceTicket.current += 1
    const ticket = evidenceTicket.current
    if (evidenceOpen) {
      setEvidence({ kind: "collapsed" })
      return
    }
    setEvidence({ kind: "loading" })
    const loaded = await loadEvidence(finding)
    if (evidenceTicket.current !== ticket) {
      return
    }
    setEvidence(
      loaded === null ? { kind: "error" } : { ...loaded, kind: "loaded" },
    )
  }

  const firstSeenLabel = formatRelativeTimestamp(finding.firstSeen)
  const lastSeenLabel = formatRelativeTimestamp(finding.lastSeen)

  return (
    <FindingCard
      finding={finding}
      // When it started, how often, and whether it is still happening: the
      // three facts that place a finding. The briefing's priority reason repeats
      // the chip or the count, so it goes to Sofia's prompt rather than the row.
      meta={
        <>
          {firstSeenLabel ? <>First seen {firstSeenLabel} · </> : null}
          Seen {finding.occurrenceCount}{" "}
          {finding.occurrenceCount === 1 ? "time" : "times"}
          {lastSeenLabel ? <> · last seen {lastSeenLabel}</> : null}
        </>
      }
    >
      <div>
        <FindingActionControls
          finding={finding}
          onAction={onAction}
          leading={
            <Button
              type="button"
              variant="ghost"
              aria-expanded={evidenceOpen}
              onClick={() => {
                void toggleEvidence()
              }}
            >
              {evidenceOpen
                ? "Hide evidence"
                : `Show evidence (${finding.evidenceCount})`}
              {evidenceOpen ? (
                <ChevronUp aria-hidden />
              ) : (
                <ChevronDown aria-hidden />
              )}
            </Button>
          }
          primaryAction={
            <AskSofiaButton
              onClick={() => {
                openCoviWithPrompt(buildFindingReviewPrompt(finding), {
                  surface: "home",
                })
              }}
            />
          }
        />
        {evidence.kind === "loading" && (
          <p className="px-1 pt-2 text-sm text-coval-text-muted" role="status">
            Loading evidence…
          </p>
        )}
        {evidence.kind === "error" && (
          <p className="px-1 pt-2 text-sm text-coval-danger-text" role="status">
            Couldn&apos;t load evidence for this finding. Try again.
          </p>
        )}
        {evidence.kind === "loaded" && (
          <FindingEvidenceList
            evidenceCount={finding.evidenceCount}
            items={evidence.items}
            truncated={evidence.truncated}
          />
        )}
      </div>
    </FindingCard>
  )
}
